/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useEffect } from "react";
import { ContextUpdater, RequestContext } from "../Context";
import { useUpdateState } from "../util/State";
import { Button, Label } from "reactstrap";
import { defaultError } from "./RequestUtil";
import { mergeDeep } from "../util/Object";
import { constants } from "../constants/Constants";
import OverlaySpinner from "../elements/indicators/OverlaySpinner";
import Modal from "../elements/modal/CenteredModal";
import debug from "../util/Debug";

const RequestManager = () => {
  // context
  const updateContexts = useContext(ContextUpdater);
  const requestContext = useContext(RequestContext);

  // error state
  const intialErrorState = { isOpen: false, message: null };
  const [errorState, updateErrorState] = useUpdateState(intialErrorState);
  const dismissError = () => updateErrorState({ isOpen: false });

  // make requests if needed
  useEffect(() => {
    const makeRequests = async (requests) => {
      // set up request list results
      const requestResults = Array.from({ length: requests.count }, () => ({}));

      // variables to be set
      let processedResults = {};
      let error = null;

      try {
        // make requests
        const pendingRequests = requests.map(item => {
          const requestAttrs = { ...item, token: requestContext.token };
          if (item.token != null) {
            requestAttrs.token = item.token
          }
          return makeRequest(requestAttrs);
        });
        const results = await Promise.all(pendingRequests);
        
        // process each response
        requests.every((item, index) => {
          // process response
          const result = item.handler(results[index]);

          // handle error
          if (!result || result.error) {
            error = result.error ?? { message: defaultError, code: null };
            return false;
          }

          // add result to list
          requestResults[index] = result;
          return true
        });

        // process successful response
        if (!error) {
          processedResults = mergeDeep(...requestResults);
        }
      } catch (exception) {
        // log exception
        debug.warn(`EXCEPTION caught makeRequests(): "${exception}"`);
        debug.warn(exception.stack);

        // prepare error
        error = { code: null, message: "Error processing server response" };
      } finally {
        // handle completion
        const { handleFailure, handleSuccess } = requestContext;

        if (error) {
          // prepare to display error modal
          const errorMessage = handleFailure?.(error.message ?? defaultError, error.code) ?? defaultError; 
          updateErrorState({ isOpen: true, message: errorMessage});
        } else {
          // call success handler if available
          handleSuccess?.(processedResults);
        }

        // update context state with results and clear requests
        const newRequestState = {
          requests: [],
          showLoading: false,
          handleSuccess: null,
          handleFailure: null,
        };

        const newToken = processedResults?.request?.token;
        if (newToken) {
          newRequestState.token = newToken;
        }
        
        updateContexts({
          ...processedResults,
          request: newRequestState
        });

        // log results
        debug.log('Request Results');
        debug.log(processedResults);
      }
    };

    // asynchronously make requests
    if (requestContext.requests?.length > 0) {
      makeRequests(requestContext.requests);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestContext]);

  return (
    <>
      <OverlaySpinner show={requestContext.showLoading} />
      <Modal isOpen={errorState.isOpen} toggle={dismissError}>
        <div align="center">
          <Label className="mb-3" tag="h4">{errorState.message}</Label>
          <Button color="blue" onClick={dismissError}>Dismiss</Button>
        </div>
      </Modal>
    </>
  );
};

const makeRequest = async ({
  server = "radius",
  route,
  method,
  token,
  payload,
  form
}) => {
  // prepare request url
  let urlBase;
  switch (server) {
    case "radius": urlBase = constants.radiusEndpointBase; break;
    case "remoteManager": urlBase = constants.remoteManagerEndpointBase; break;
    default: debug.error("Invalid server + " + server); return null;
  }
  const reqUrl = urlBase + route;

  // headers
  const headerObj = {
    ...(payload != null ? { "Content-Type": "application/json" } : {}),
    ...(token != null ? {
      "Authorization": `Bearer ${token}`,
      "Access-Control-Request-Headers": "Authorization, Content-Type"
    } : {})
  };
  const headers = new Headers(headerObj);

  // body
  let body = undefined;
  if (payload) {
    body = JSON.stringify(payload);
  }
  else if (form) {
    body = form;
  }

  const requestParameters = {
    method: method ?? "POST",
    headers: headers,
    mode: "cors",
    body: body
  };

  try {
    // make request
    const response = await fetch(reqUrl, requestParameters);

    // check content type
    const contentType = response.headers.get("content-type");

    // parse response based on type
    let resp;
    if (contentType == null || contentType.includes("application/json")) {
      const text = await response.text();
      resp = JSON.parse(text);
    }
    else if (
      contentType.includes("text/plain") ||
      contentType.includes("text/html") ||
      contentType.includes("application/x-x509-ca-cert") ||
      contentType.includes("application/octet-stream"))
    {
      const text = await response.text();
      resp = text;
    }
    else if (contentType.includes("application/zip")) {
      const blob = await response.blob();
      resp = blob;
    }
    else {
      debug.warn("Response content type not supported: " + contentType);
    }

    // session expired
    if (resp.status === "session_expired") {
      return { sessionExpired: resp.message };
    }

    // request failed
    if (!response.ok || resp.status === "error") {
      // parse out error if available
      const errorMessage = resp.error ?? defaultError;
      const error = { code: response.status, message: errorMessage};

      // return error
      return { error: error, response: resp };
    }

    // successful request
    return { response: resp };
  } catch (exception) {
    debug.warn(`EXCEPTION caught in makeRequest(): "${exception}"`);
    debug.warn(exception.stack);
    return { error: { code: null, message: "Invalid server response" } };
  }
};

export default RequestManager;
